import type { FC } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Typography, useTheme } from '@mui/material'
import { type DiffStats, getFileNameFromPath } from '@/features/events/shared/utils'
import { FIXED_EVENT_LABEL_SIZE } from './fixedTypographyStyles'
import { diffStatsAddedSx, diffStatsRemovedSx } from '@/features/events/shared/styles'

interface FileInfoProps {
  readonly filePath: string
  readonly showPath?: boolean
  readonly stats?: DiffStats
}

export const FileInfo: FC<FileInfoProps> = ({ filePath, showPath = true, stats }) => {
  const theme = useTheme()
  const fileName = getFileNameFromPath(filePath)
  const [isPathExpanded, setIsPathExpanded] = useState(false)
  const pathRef = useRef<HTMLDivElement>(null)

  const needsTruncation = useMemo(
    () => filePath.length > PATH_TRUNCATE_THRESHOLD,
    [filePath.length],
  )
  const displayPath = useMemo(() => {
    if (!needsTruncation || isPathExpanded) {
      return filePath
    }
    return truncatePath(filePath)
  }, [filePath, needsTruncation, isPathExpanded])

  useEffect(() => {
    if (!isPathExpanded) return

    const handleClickOutside = (event: MouseEvent) => {
      if (pathRef.current && !pathRef.current.contains(event.target as Node)) {
        setIsPathExpanded(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isPathExpanded])

  const hasAdds = stats && stats.linesAdded > 0
  const hasRemoves = stats && stats.linesRemoved > 0

  const pathTooltip = useMemo(() => {
    if (!needsTruncation) {
      return undefined
    }
    return isPathExpanded ? 'Click to collapse' : 'Click to expand'
  }, [needsTruncation, isPathExpanded])

  return (
    <Box sx={infoContainerSx}>
      <Box sx={fileNameRowSx}>
        <Box sx={infoRowSx}>
          <Typography variant="caption" sx={labelSx}>
            Name:
          </Typography>
          <Typography variant="body2" sx={fileNameValueSx(theme)}>
            {fileName}
          </Typography>
        </Box>

        {stats && (hasAdds || hasRemoves) && (
          <Box sx={statsContainerSx}>
            {hasAdds && (
              <Typography
                component="span"
                sx={diffStatsAddedSx(theme, { fontSize: FIXED_EVENT_LABEL_SIZE })}
              >
                +{stats.linesAdded}
              </Typography>
            )}
            {hasRemoves && (
              <Typography
                component="span"
                sx={diffStatsRemovedSx(theme, { fontSize: FIXED_EVENT_LABEL_SIZE })}
              >
                -{stats.linesRemoved}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {showPath && (
        <Box sx={infoRowSx}>
          <Typography variant="caption" sx={labelSx}>
            Path:
          </Typography>
          <Box ref={pathRef} sx={{ display: 'inline' }}>
            <Typography
              variant="body2"
              onClick={() => needsTruncation && setIsPathExpanded(!isPathExpanded)}
              sx={filePathValueSx(theme, needsTruncation)}
              title={pathTooltip}
            >
              {displayPath}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  )
}

const infoContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
  mt: 1,
  mb: 2,
}

const fileNameRowSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}

const infoRowSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'baseline',
  gap: 1,
}

const statsContainerSx: SxProps<Theme> = {
  display: 'flex',
  gap: 0.5,
  alignItems: 'center',
}

const labelSx: SxProps<Theme> = {
  fontWeight: 600,
  color: 'text.secondary',
  flexShrink: 0,
  fontSize: 'inherit',
}

const fileNameValueSx = (theme: Theme): SxProps<Theme> => ({
  fontFamily: 'monospace',
  color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.main,
  wordBreak: 'break-all',
  fontSize: 'inherit',
})

const filePathValueSx = (theme: Theme, isClickable: boolean): SxProps<Theme> => ({
  fontFamily: 'monospace',
  color: theme.palette.text.secondary,
  opacity: 0.7,
  cursor: isClickable ? 'pointer' : 'default',
  transition: 'color 0.2s, opacity 0.2s',
  '&:hover': isClickable
    ? {
        color: theme.palette.primary.main,
        opacity: 1,
      }
    : {},
  fontSize: 'inherit',
})

const PATH_TRUNCATE_THRESHOLD = 80

function truncatePath(path: string): string {
  if (path.length <= PATH_TRUNCATE_THRESHOLD) {
    return path
  }

  const separator = path.includes('/') ? '/' : '\\'
  const parts = path.split(separator)

  if (parts.length <= 3) {
    return path
  }

  const firstPart = parts.slice(0, 2).join(separator)
  const lastParts = parts.slice(-3).join(separator)

  return `${firstPart}/.../${lastParts}`
}
